import React from 'react';
import classNames from 'classnames';
import { X, AlertTriangle } from 'lucide-react';
import { Input } from '../../../atoms/Input';
import Button from '../../Button/Button';
import PurchaserToggleBlock from './PurchaserToggleBlock';
import Banner from '../../Banner';
import useSecureCardForm from './useSecureCardForm';
import { SecureCardPayload } from '../../../redux/payment_methods';
import IframeInput from './IframeInput';
import Spinner from '../../../foundations/Spinner';

type SecureCardFormProps = {
  params: { retailerId: string };
  purchaserId?: string;
  onSuccess?: () => void;
  onClose?: () => void;
  isStickyFooter?: boolean;
  addCardPayment: (cardDetails: SecureCardPayload, retailerId: string) => void;
  hasDefaultForAllToggle?: boolean;
  hideCancel?: boolean;
  /** Callback fired when submitting state changes - useful for parent components to react to form submission */
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export const SecureCardForm = (props: SecureCardFormProps) => {
  const {
    onClose,
    isStickyFooter = false,
    hasDefaultForAllToggle = false,
    hideCancel = false,
    onSubmittingChange,
  } = props;

  const {
    values,
    errors,
    iframeErrors,
    dirty,
    isSubmitting,
    handleSubmit,
    setFieldValue,
    resetForm,
    handleCancel,
    handleExpiryDateChange,
    isSDKReady,
    isInitialized,
    isSessionLoading,
    cardNumberElRef,
    cvvElRef,
  } = useSecureCardForm({
    ...props,
    onSubmittingChange, // Pass through to hook for event-driven notification
  });

  return (
    <div className="relative flex flex-col gap-xxl rounded-xl bg-regular">
      {/* Processing overlay - shown when card is being submitted */}
      {/* Form must remain in DOM for Spreedly iframes to work during tokenization */}
      {isSubmitting && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center gap-xxl bg-regular p-s text-center"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <Spinner size="XXL" color="orange" />
          <div className="flex flex-col gap-s">
            <h3 className="font-heading text-h3 font-black text-strong">
              Processing your card…
            </h3>
            <p className="font-body text-body text-subtle">
              Please wait while we securely process your card details. This may
              take a few seconds. Do not close this window.
            </p>
          </div>
        </div>
      )}

      <form
        role="form"
        onSubmit={handleSubmit}
        className={isSubmitting ? 'invisible' : ''}
        aria-hidden={isSubmitting}
      >
        <div className="flex flex-col gap-xl">
          {/* Title Section */}
          <div className="flex flex-col gap-m">
            <div className="flex items-start gap-xxxl">
              <div className="flex flex-1 flex-col gap-xxxs">
                <h3 className="font-heading text-h3 font-bold leading-8 text-strong">
                  Add payment details
                </h3>
                <p className="font-body text-body-small leading-5 text-subtle">
                  Enter your card details securely in the fields below.
                </p>
              </div>
              {onClose && (
                <Button
                  type="button"
                  variant="tertiary"
                  circle
                  onClick={onClose}
                  aria-label="Close"
                  icon={<X className="size-full" />}
                />
              )}
            </div>
          </div>

          {/* Form Fields */}
          <div className="flex flex-col gap-m">
            {/* Name on card */}
            <Input
              tag="cardName"
              inputLabel="Name on card"
              size="max"
              value={values.cardName}
              hasError={!!errors.cardName}
              errorLabel={errors.cardName}
              onChange={setFieldValue}
              onBlur={setFieldValue}
            />

            {/* Card number - Spreedly iframe */}
            <IframeInput
              id="spreedly-number"
              label="Card number"
              inputRef={cardNumberElRef}
              isInitialized={isInitialized}
              isLoading={isSessionLoading || !isSDKReady}
              loadingText="Loading secure payment form..."
              initializingText="Initializing secure payment form..."
              error={iframeErrors.cardNumber}
              reserveErrorSpace={false}
            />

            {/* Expiry date and CVV */}
            <div className="flex flex-col items-end md:flex-row md:gap-m">
              {/* Expiry date */}
              <div className="flex w-full flex-col gap-xxs md:flex-1">
                <Input
                  tag="expiryDate"
                  inputLabel="Expiry date"
                  hint="MM/YY"
                  placeholder="MM/YY"
                  size="max"
                  value={values.expiryDate}
                  hasError={!!errors.expiryDate}
                  errorLabel={errors.expiryDate}
                  onChange={handleExpiryDateChange}
                  onBlur={handleExpiryDateChange}
                />
                {/* Reserve space for error message to match CVV height */}
                {!errors.expiryDate && (
                  <span className="invisible font-body text-label-small font-bold">
                    &nbsp;
                  </span>
                )}
              </div>

              {/* CVV - Spreedly iframe */}
              <IframeInput
                id="spreedly-cvv"
                label="CVV"
                hint="" // Empty hint to reserve space and align with Expiry date
                inputRef={cvvElRef}
                isInitialized={isInitialized}
                isLoading={isSessionLoading || !isSDKReady}
                loadingText="Loading..."
                initializingText="Initializing..."
                error={iframeErrors.cvv}
                className="w-full md:flex-1"
              />
            </div>
          </div>

          {/* Error Banner */}
          {errors.submit && (
            <div role="alert" aria-live="assertive" aria-atomic="true">
              <Banner
                variant="error"
                logo={
                  <div className="flex shrink-0 items-center justify-center text-error-foreground">
                    <AlertTriangle strokeWidth={2} aria-hidden="true" />
                  </div>
                }
              >
                <span className="font-body text-body-small font-normal text-regular">
                  {errors.submit}
                </span>
              </Banner>
            </div>
          )}

          {/* Info Banner */}
          <Banner
            variant="info"
            actions={{
              alignment: 'start',
              items: [
                <Button
                  key="terms"
                  as="a"
                  href="https://www.ordermentum.com/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text-link"
                >
                  Terms and conditions
                </Button>,
              ],
            }}
          >
            <div className="flex flex-col gap-s font-body text-body-small font-normal text-regular">
              <p>
                Financial information and payments are securely encrypted before
                being stored.
              </p>
              <p>
                You give authorisation in accordance with Ordermentum terms and
                conditions listed on the website to debit your bank or credit
                card for payment.
              </p>
            </div>
          </Banner>

          {/* Default Payment Toggle */}
          {hasDefaultForAllToggle && (
            <PurchaserToggleBlock
              type="card"
              allowToggleAll
              formikFunction={checked => {
                setFieldValue('defaultAll', checked);
              }}
              formikValue={values.defaultAll}
            />
          )}
        </div>

        {/* CTA Buttons */}
        {isStickyFooter ? (
          <div
            className={classNames(
              'sticky bottom-0 mt-xl border-t border-foreground-subtle px-xxl py-s bg-regular',
              dirty ? 'block' : 'hidden'
            )}
          >
            <div className="flex justify-end gap-m">
              <Button variant="secondary" type="button" onClick={resetForm}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={isSubmitting}
                disabled={!isInitialized || isSubmitting}
              >
                Save card details
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-xl flex flex-col items-center gap-xxs md:flex-row md:justify-end">
            {!hideCancel && (
              <Button
                variant="tertiary"
                type="button"
                onClick={handleCancel}
                className="order-2 md:order-1"
              >
                Cancel
              </Button>
            )}
            <Button
              variant="primary"
              type="submit"
              disabled={!isInitialized || isSubmitting}
              loading={isSubmitting}
              className="order-1 w-full md:order-2 md:w-auto"
            >
              Save card details
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default SecureCardForm;
